import React from 'react';
import { Calculator, DollarSign, Layers, Users, BarChart3, Target } from 'lucide-react';
import FeatureFlipCard from './FeatureFlipCard';
import FlipAnalyzerMockup from './mockups/FlipAnalyzerMockup';
import ExpenseTrackerMockup from './mockups/ExpenseTrackerMockup';
import DashboardMockup from './mockups/DashboardMockup';
import TeamViewMockup from './mockups/TeamViewMockup';
import PortfolioMockup from './mockups/PortfolioMockup';
import KanbanMockup from './mockups/KanbanMockup';

const features = [
  {
    icon: Calculator,
    title: 'Never Overpay for a Deal',
    desc: 'Built-in flip calculator with ARV, rehab estimates, and ROI projections so you know your numbers before you make an offer.',
    tag: 'Deal Analysis',
    color: 'from-emerald-500/20 to-emerald-600/10',
    iconColor: 'text-emerald-400',
    Mockup: FlipAnalyzerMockup,
  },
  {
    icon: DollarSign,
    title: 'Stop Budget Overruns',
    desc: 'Track every dollar of your rehab in real time. Categorize expenses, scan receipts, and get alerts before you go over budget.',
    tag: 'Expense Control',
    color: 'from-amber-500/20 to-amber-600/10',
    iconColor: 'text-amber-400',
    Mockup: ExpenseTrackerMockup,
  },
  {
    icon: Layers,
    title: 'One Dashboard, Every Project',
    desc: 'See all your active flips, tasks, timelines, and financials in one place — no more juggling spreadsheets and notebooks.',
    tag: 'Organization',
    color: 'from-blue-500/20 to-blue-600/10',
    iconColor: 'text-blue-400',
    Mockup: DashboardMockup,
  },
  {
    icon: Users,
    title: 'Keep Your Whole Team Aligned',
    desc: 'Invite contractors, realtors, and partners. Assign roles, share project access, and collaborate without the back-and-forth.',
    tag: 'Collaboration',
    color: 'from-violet-500/20 to-violet-600/10',
    iconColor: 'text-violet-400',
    Mockup: TeamViewMockup,
  },
  {
    icon: BarChart3,
    title: "Know What's Actually Working",
    desc: 'Portfolio analytics show your profit trends, ROI by zip code, and rehab cost per sqft — so you double down on what works.',
    tag: 'Analytics',
    color: 'from-cyan-500/20 to-cyan-600/10',
    iconColor: 'text-cyan-400',
    Mockup: PortfolioMockup,
  },
  {
    icon: Target,
    title: 'Close Leads, Not Lose Them',
    desc: 'Pipeline management from lead intake to acquisition. Track every opportunity, set follow-ups, and never let a deal slip.',
    tag: 'Lead Pipeline',
    color: 'from-rose-500/20 to-rose-600/10',
    iconColor: 'text-rose-400',
    Mockup: KanbanMockup,
  },
];

export { features };

export default function FeatureFlipCards() {
  return (
    <section className="py-14 px-6" id="features">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
            Every Tool You Need. One Platform.
          </h2>
          <p className="text-base text-slate-400 max-w-xl mx-auto mb-2">
            Replace your spreadsheets, notebooks, and disconnected apps with a single system built
            for house flippers.
          </p>
          <p className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
            <span className="hidden sm:inline">Hover over a card to preview the feature</span>
            <span className="sm:hidden">Tap a card to preview the feature</span>
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, i) => (
            <FeatureFlipCard key={i} {...feature} MockupComponent={feature.Mockup} />
          ))}
        </div>
      </div>
    </section>
  );
}
